import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { iconImg } from './icons';
import { bindModalDismiss, createModalShell } from './modalDialogShell';

export async function showRecoveryExportDialog(exportText: string): Promise<void> {
  let saved = false;

  const shell = createModalShell({
    titleKey: 'settings.recovery.dialogTitle',
    titleId: 'recovery-export-dialog-title',
    helpTopic: dialogHelpTopic('recoveryExport'),
  });

  shell.body.innerHTML = `
    <div class="warning-panel">
      <span class="warning-panel__icon"></span>
      <span data-i18n="settings.recovery.linkWarning"></span>
    </div>
    <p class="muted" data-i18n="settings.recovery.dialogBody"></p>
    <div class="link-box recovery-export-actions">
      <textarea id="recovery-export-text" class="message-input recovery-export-text" readonly></textarea>
      <div class="recovery-export-buttons">
        <button type="button" class="button primary" id="recovery-export-copy">
          <span data-i18n="settings.recovery.copy"></span>
        </button>
        <button type="button" class="button ghost" id="recovery-export-download">
          <span data-i18n="settings.recovery.download"></span>
        </button>
      </div>
    </div>
    <p class="error-text" id="recovery-export-copy-warning" hidden data-i18n="settings.recovery.copyReminder"></p>
  `;

  shell.actions.innerHTML = `
    <button type="button" class="button ghost" id="recovery-export-close">
      <span data-i18n="info.owner.dialogClose"></span>
    </button>
  `;

  document.body.append(shell.backdrop);
  shell.body.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
  applyI18n(shell.dialog);

  const textarea = shell.body.querySelector('#recovery-export-text') as HTMLTextAreaElement;
  textarea.value = exportText;

  const copyWarning = shell.body.querySelector('#recovery-export-copy-warning') as HTMLParagraphElement;
  const copyLabel = shell.body.querySelector('#recovery-export-copy [data-i18n]');
  let closeAttempts = 0;

  const markSaved = () => {
    if (saved) {
      return;
    }

    saved = true;
    copyWarning.hidden = true;
  };

  const showCopiedFeedback = () => {
    if (copyLabel instanceof HTMLElement) {
      copyLabel.textContent = t('settings.recovery.copied');
      window.setTimeout(() => {
        copyLabel.setAttribute('data-i18n', 'settings.recovery.copy');
        applyI18n(shell.dialog);
      }, 2000);
    }
  };

  const markCopied = () => {
    if (saved) {
      return;
    }

    saved = true;
    copyWarning.hidden = true;
    showCopiedFeedback();
  };

  const closeDialog = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const tryClose = () => {
    if (!saved) {
      copyWarning.hidden = false;
      closeAttempts += 1;
      if (closeAttempts >= 2) {
        closeDialog();
      }
      return;
    }

    closeDialog();
  };

  const refreshLocale = () => {
    if (saved && copyLabel instanceof HTMLElement && copyLabel.textContent === t('settings.recovery.copied')) {
      copyLabel.textContent = t('settings.recovery.copied');
    }
    applyI18n(shell.dialog);
  };

  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: tryClose,
  });

  shell.body.querySelector('#recovery-export-download')?.addEventListener('click', () => {
    const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'shareweb-recovery.txt';
    anchor.click();
    URL.revokeObjectURL(url);
    markSaved();
  });

  shell.body.querySelector('#recovery-export-copy')?.addEventListener('click', async () => {
    await navigator.clipboard.writeText(exportText);
    markCopied();
  });

  textarea.addEventListener('copy', () => {
    markCopied();
  });

  shell.actions.querySelector('#recovery-export-close')?.addEventListener('click', tryClose);
}
